import type { FinancialPeriod } from "@inrev/common";

// Balance sheet row ids, matching the ids produced by buildBalanceSheetViewModel. A group row id
// encodes its line-item field key as `bs::<section>::<category>::<key>::group`. Building the id from
// the source-of-truth section-key types in `@inrev/common` makes a renamed or removed field a compile
// error here, instead of an id that silently resolves to `null` at runtime.
const currentAssetGroup = (key: FinancialPeriod.CurrentAssetKey): string =>
	`bs::assets::current::${key}::group`;
const currentLiabilityGroup = (key: FinancialPeriod.CurrentLiabilityKey): string =>
	`bs::liabilities::current::${key}::group`;
const nonCurrentLiabilityGroup = (key: FinancialPeriod.NonCurrentLiabilityKey): string =>
	`bs::liabilities::nonCurrent::${key}::group`;

// A category subtotal id is `bs::<section>::<category>::__subtotal`. Binding the category to the
// view's section nesting makes a renamed/removed category a compile error here too. Equity has no
// such helper: its subtotal lives under a synthetic `main` grid category with no view counterpart.
type BalanceSheetView = FinancialPeriod.BalanceSheetView;
const assetCategorySubtotal = (
	category: Exclude<keyof BalanceSheetView["assets"], "total">,
): string => `bs::assets::${category}::__subtotal`;
const liabilityCategorySubtotal = (
	category: Exclude<keyof BalanceSheetView["liabilities"], "total">,
): string => `bs::liabilities::${category}::__subtotal`;

export const BS = {
	cash: currentAssetGroup("cash"),
	marketableSecurities: currentAssetGroup("marketableSecurities"),
	accountsReceivable: currentAssetGroup("accountsReceivable"),
	contractAssets: currentAssetGroup("contractAssets"),
	retainage: currentAssetGroup("retainage"),
	underbillings: currentAssetGroup("underbillings"),
	currentAssets: assetCategorySubtotal("current"),
	accountsPayable: currentLiabilityGroup("accountsPayable"),
	contractLiabilities: currentLiabilityGroup("contractLiabilities"),
	retainagePayable: currentLiabilityGroup("retainagePayable"),
	overbillings: currentLiabilityGroup("overbillings"),
	customerDeposits: currentLiabilityGroup("customerDeposits"),
	accruedExpenses: currentLiabilityGroup("accruedExpenses"),
	accruedLossesOnContracts: currentLiabilityGroup("accruedLossesOnContracts"),
	creditCardsBalance: currentLiabilityGroup("creditCardsBalance"),
	cashOverdraft: currentLiabilityGroup("cashOverdraft"),
	blocBalance: currentLiabilityGroup("blocBalance"),
	currentPortionOfLtd: currentLiabilityGroup("currentPortionOfLtd"),
	otherNotesPayable: currentLiabilityGroup("otherNotesPayable"),
	taxesPayable: currentLiabilityGroup("taxesPayable"),
	taxesDeferred: currentLiabilityGroup("taxesDeferred"),
	relatedPartyPayable: currentLiabilityGroup("relatedPartyPayable"),
	shareholderEmplPayable: currentLiabilityGroup("shareholderEmplPayable"),
	currentPortionOfRouLiabilities: currentLiabilityGroup("currentPortionOfRouLiabilities"),
	otherCurrentLiabilities: currentLiabilityGroup("otherCurrentLiabilities"),
	currentLiabilities: liabilityCategorySubtotal("current"),
	ltdBankTermLoan: nonCurrentLiabilityGroup("ltdBankTermLoan"),
	ltdRealEstate: nonCurrentLiabilityGroup("ltdRealEstate"),
	ltdEquipment: nonCurrentLiabilityGroup("ltdEquipment"),
	ltdOther: nonCurrentLiabilityGroup("ltdOther"),
	ltdShareholder: nonCurrentLiabilityGroup("ltdShareholder"),
	ltdRelatedParty: nonCurrentLiabilityGroup("ltdRelatedParty"),
	// Section subtotal ids emitted directly by buildBalanceSheetViewModel (TOTAL_ASSETS_ID /
	// TOTAL_LIABILITIES_ID), not derived from a section-key type, so they stay plain literals.
	totalAssets: "bs::total-assets",
	totalLiabilities: "bs::total-liabilities",
	// Synthetic `main` grid category, not a view-model key, so it stays a plain literal.
	equity: "bs::equity::main::__subtotal",
} as const;
